import { useEffect, useState } from 'preact/hooks';
import {
    createCollection,
    deleteCollection,
    listMyCollections,
    updateCollection,
    type CollectionResponse,
    type CollectionVisibility,
} from '../../api/collections';
import { ErrorTile, Eyebrow } from '../../primitives';
import { AccountShell } from '../../primitives/account';
import { useToast } from '../../primitives/admin';
import { useTranslation } from '../../i18n';

/**
 * Collections section of /me/account. Lists every collection the user
 * owns (regardless of visibility) with inline rename / visibility /
 * delete affordances, plus a "create new" form at the top. Adding
 * members happens from the work detail page (the catalog is
 * naturally where you find works to add); this surface is for
 * collection-level CRUD.
 */
export function AccountCollectionsPage() {
    const t = useTranslation();
    const [collections, setCollections] = useState<CollectionResponse[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const toast = useToast();

    const refresh = async () => {
        try {
            const list = await listMyCollections();
            setCollections(list);
            setLoadError(null);
        } catch (e) {
            setLoadError(e instanceof Error ? e.message : String(e));
        }
    };

    useEffect(() => {
        void refresh();
    }, []);

    const onCreate = async (slug: string, name: string) => {
        setBusyKey('create');
        try {
            await createCollection({ slug, name });
            await refresh();
            toast.push('success', <>{t('me.collections.createSuccess', { name })}</>);
        } catch (e) {
            toast.push('error', t('me.collections.createError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusyKey(null);
        }
    };

    const onUpdate = async (
        slug: string,
        patch: Partial<{ name: string; description: string; visibility: CollectionVisibility }>,
    ) => {
        setBusyKey(slug);
        try {
            await updateCollection(slug, {
                slug,
                name: patch.name ?? '',
                description: patch.description,
                visibility: patch.visibility,
            });
            await refresh();
            toast.push('success', <>{t('me.collections.updateSuccess')}</>);
        } catch (e) {
            toast.push('error', t('me.collections.updateError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusyKey(null);
        }
    };

    const onDelete = async (col: CollectionResponse) => {
        if (
            !confirm(
                t('me.collections.deleteConfirm', { name: col.name, count: col.memberCount }),
            )
        )
            return;
        setBusyKey(col.slug);
        try {
            await deleteCollection(col.slug);
            await refresh();
            toast.push('success', <>{t('me.collections.deleteSuccess', { name: col.name })}</>);
        } catch (e) {
            toast.push('error', t('me.collections.deleteError', { msg: e instanceof Error ? e.message : String(e) }));
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <AccountShell
            title={t('me.collections.title')}
            subtitle={t('me.collections.subtitle')}
        >
            <div class="mx-auto w-full max-w-3xl space-y-10 px-[clamp(20px,3.5vw,56px)] py-10">
                {loadError && (
                    <ErrorTile title={t('me.collections.loadError')} details={loadError} />
                )}

                <CreateCollectionForm onCreate={onCreate} busy={busyKey === 'create'} />

                <section>
                    <Eyebrow class="mb-3">
                        {t('me.collections.listEyebrow')}{' '}
                        <span class="text-fg-faint">
                            ({collections?.length ?? 0})
                        </span>
                    </Eyebrow>
                    {collections === null ? (
                        <p class="text-sm text-fg-muted">{t('me.collections.loading')}</p>
                    ) : collections.length === 0 ? (
                        <p class="text-sm text-fg-muted">
                            {t('me.collections.empty')}
                        </p>
                    ) : (
                        <ul class="space-y-3">
                            {collections.map((c) => (
                                <CollectionRow
                                    key={c.id}
                                    collection={c}
                                    busy={busyKey === c.slug}
                                    onUpdate={onUpdate}
                                    onDelete={onDelete}
                                />
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </AccountShell>
    );
}

function CreateCollectionForm({
    onCreate,
    busy,
}: {
    onCreate: (slug: string, name: string) => void | Promise<void>;
    busy: boolean;
}) {
    const t = useTranslation();
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');

    const onSubmit = async (e: SubmitEvent) => {
        e.preventDefault();
        const finalSlug =
            slug.trim() ||
            name
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
        if (!finalSlug || !name.trim()) return;
        await onCreate(finalSlug, name.trim());
        setName('');
        setSlug('');
    };

    return (
        <section>
            <Eyebrow class="mb-3">{t('me.collections.newEyebrow')}</Eyebrow>
            <form onSubmit={onSubmit} class="space-y-3">
                <div class="flex flex-wrap gap-2">
                    <input
                        type="text"
                        value={name}
                        onInput={(e) =>
                            setName((e.target as HTMLInputElement).value)
                        }
                        placeholder={t('me.collections.namePlaceholder')}
                        required
                        class="flex-1 min-w-[240px] rounded border border-rule bg-surface-raised px-3 py-1.5 text-sm text-fg-primary outline-none focus:border-accent"
                    />
                    <input
                        type="text"
                        value={slug}
                        onInput={(e) =>
                            setSlug((e.target as HTMLInputElement).value)
                        }
                        placeholder={t('me.collections.slugPlaceholder')}
                        pattern="[a-z0-9-]+"
                        class="w-48 rounded border border-rule bg-surface-raised px-3 py-1.5 font-mono text-sm text-fg-primary outline-none focus:border-accent"
                    />
                    <button
                        type="submit"
                        disabled={busy}
                        class="rounded border border-accent bg-accent px-4 py-1.5 text-sm font-medium text-surface-base transition-colors hover:bg-fg-primary disabled:opacity-50"
                    >
                        {busy ? t('me.collections.creating') : t('me.collections.createCta')}
                    </button>
                </div>
                <p class="text-xs text-fg-muted">
                    {t('me.collections.slugHint')}
                </p>
            </form>
        </section>
    );
}

function CollectionRow({
    collection,
    busy,
    onUpdate,
    onDelete,
}: {
    collection: CollectionResponse;
    busy: boolean;
    onUpdate: (
        slug: string,
        patch: Partial<{
            name: string;
            description: string;
            visibility: CollectionVisibility;
        }>,
    ) => void | Promise<void>;
    onDelete: (col: CollectionResponse) => void | Promise<void>;
}) {
    const t = useTranslation();
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(collection.name);
    const [description, setDescription] = useState(collection.description ?? '');

    return (
        <li class="rounded border border-rule bg-surface-raised p-3">
            <div class="flex flex-wrap items-baseline gap-2">
                <a
                    href={`/u/${encodeURIComponent('me')}/c/${encodeURIComponent(collection.slug)}`}
                    class="font-medium text-fg-primary hover:text-accent"
                    title={t('me.collections.openTitle')}
                >
                    {collection.name}
                </a>
                <code class="font-mono text-[11px] text-fg-muted">
                    {collection.slug}
                </code>
                <span class="ml-auto font-mono text-[11px] uppercase tracking-[0.14em] text-fg-faint">
                    {t(`common.visibility.${collection.visibility.toLowerCase()}`)} · {collection.memberCount}{' '}
                    {collection.memberCount === 1 ? t('me.collections.pkg') : t('me.collections.pkgs')}
                </span>
            </div>
            {!editing && collection.description && (
                <p class="mt-1 text-sm text-fg-muted">{collection.description}</p>
            )}
            {editing && (
                <div class="mt-3 space-y-2">
                    <input
                        type="text"
                        value={name}
                        onInput={(e) =>
                            setName((e.target as HTMLInputElement).value)
                        }
                        class="w-full rounded border border-rule bg-surface-base px-2 py-1 text-sm text-fg-primary outline-none focus:border-accent"
                    />
                    <textarea
                        value={description}
                        onInput={(e) =>
                            setDescription((e.target as HTMLTextAreaElement).value)
                        }
                        rows={2}
                        class="w-full rounded border border-rule bg-surface-base px-2 py-1 text-sm text-fg-primary outline-none focus:border-accent"
                        placeholder={t('me.collections.descriptionPlaceholder')}
                    />
                </div>
            )}
            <div class="mt-3 flex flex-wrap gap-2">
                {editing ? (
                    <>
                        <button
                            type="button"
                            onClick={async () => {
                                await onUpdate(collection.slug, {
                                    name,
                                    description,
                                });
                                setEditing(false);
                            }}
                            disabled={busy}
                            class="rounded border border-accent bg-accent px-3 py-1 text-xs font-medium text-surface-base disabled:opacity-50"
                        >
                            {t('me.collections.saveCta')}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setName(collection.name);
                                setDescription(collection.description ?? '');
                                setEditing(false);
                            }}
                            class="rounded border border-rule-strong px-3 py-1 text-xs text-fg-primary"
                        >
                            {t('me.collections.cancelCta')}
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={() => setEditing(true)}
                            class="rounded border border-rule-strong px-3 py-1 text-xs text-fg-primary hover:border-accent"
                        >
                            {t('me.collections.renameCta')}
                        </button>
                        {(['Public', 'Unlisted', 'Private'] as const).map((v) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() =>
                                    onUpdate(collection.slug, {
                                        name: collection.name,
                                        visibility: v,
                                    })
                                }
                                disabled={busy || collection.visibility === v}
                                class={`rounded border px-2 py-1 text-xs transition-colors ${
                                    collection.visibility === v
                                        ? 'cursor-default border-accent text-accent'
                                        : 'border-rule-strong text-fg-muted hover:border-accent hover:text-fg-primary'
                                }`}
                                title={t('me.collections.visibilityTitle', { v: t(`common.visibility.${v.toLowerCase()}`) })}
                            >
                                {t(`common.visibility.${v.toLowerCase()}`)}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => onDelete(collection)}
                            disabled={busy}
                            class="rounded border border-rule-strong px-3 py-1 text-xs text-fg-muted transition-colors hover:border-state-failed hover:text-state-failed disabled:opacity-50"
                        >
                            {t('me.collections.deleteCta')}
                        </button>
                    </>
                )}
            </div>
        </li>
    );
}
